"use client";

import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, FileCode2, Play, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import { generateMetaTags, type MetaTagInput } from "@/lib/tools/meta";
import { generateRobotsTxt, type RobotsPreset } from "@/lib/tools/robots";
import { buildUtmUrl, type UtmInput } from "@/lib/tools/utm";

const optionalUrl = z
  .string()
  .trim()
  .optional()
  .refine((value) => {
    if (!value) {
      return true;
    }

    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  }, "Enter a valid absolute URL.");

const utmSchema = z.object({
  url: z.string().trim().url("Enter a valid absolute URL."),
  source: z.string().trim().optional(),
  medium: z.string().trim().optional(),
  campaign: z.string().trim().optional(),
  term: z.string().trim().optional(),
  content: z.string().trim().optional(),
});

type UtmFormValues = z.infer<typeof utmSchema>;

export function UtmBuilderTool() {
  const [output, setOutput] = useState("");
  const [toolError, setToolError] = useState("");
  const form = useForm<UtmFormValues>({
    resolver: zodResolver(utmSchema),
    defaultValues: {
      url: "",
      source: "",
      medium: "",
      campaign: "",
      term: "",
      content: "",
    },
  });

  const submit = (values: UtmFormValues) => {
    const result = buildUtmUrl(values as UtmInput);
    setOutput(result.ok ? result.value : "");
    setToolError(result.ok ? "" : result.error);
  };

  const reset = () => {
    form.reset();
    setOutput("");
    setToolError("");
  };

  const errors = form.formState.errors;

  return (
    <InputOutputPanel
      inputTitle="Campaign fields"
      outputTitle="Final URL"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              form.reset({
                  url: "https://local-dev-tools.com/tools",
                source: "newsletter",
                medium: "email",
                campaign: "mvp-launch",
                term: "developer-tools",
                content: "hero-cta",
              })
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={reset} />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <form className="space-y-4" onSubmit={form.handleSubmit(submit)}>
          <div className="space-y-2">
            <Label htmlFor="utm-url">URL</Label>
            <Input id="utm-url" placeholder="https://example.com/page" {...form.register("url")} />
            <ErrorMessage message={errors.url?.message} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {(["source", "medium", "campaign", "term", "content"] as const).map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={`utm-${field}`}>{field}</Label>
                <Input id={`utm-${field}`} {...form.register(field)} />
              </div>
            ))}
          </div>
          <ErrorMessage message={toolError} />
          <Button type="submit">
            <Play className="h-4 w-4" />
            Generate URL
          </Button>
        </form>
      }
      output={<CodeOutput value={output} placeholder="Generated campaign URL will appear here." />}
    />
  );
}

const metaSchema = z.object({
  title: z.string().trim().min(1, "Title is required."),
  description: z.string().trim().min(1, "Description is required."),
  canonicalUrl: optionalUrl,
});

type MetaFormValues = z.infer<typeof metaSchema>;

export function MetaTagGeneratorTool() {
  const [output, setOutput] = useState("");
  const form = useForm<MetaFormValues>({
    resolver: zodResolver(metaSchema),
    defaultValues: {
      title: "",
      description: "",
      canonicalUrl: "",
    },
  });
  const errors = form.formState.errors;

  const submit = (values: MetaFormValues) => {
    setOutput(generateMetaTags(values));
  };

  return (
    <InputOutputPanel
      inputTitle="Metadata"
      outputTitle="HTML tags"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              form.reset({
                title: "Privacy-first tools for developers and makers",
                description:
                  "Format JSON, decode JWTs, generate hashes, build UTM links, and preview metadata locally.",
                  canonicalUrl: "https://local-dev-tools.com/",
              })
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              form.reset();
              setOutput("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <form className="space-y-4" onSubmit={form.handleSubmit(submit)}>
          <div className="space-y-2">
            <Label htmlFor="meta-title">Title</Label>
            <Input id="meta-title" {...form.register("title")} />
            <ErrorMessage message={errors.title?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-description">Description</Label>
            <Textarea
              id="meta-description"
              className="min-h-28 font-sans"
              {...form.register("description")}
            />
            <ErrorMessage message={errors.description?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="meta-canonical">Canonical URL</Label>
            <Input id="meta-canonical" {...form.register("canonicalUrl")} />
            <ErrorMessage message={errors.canonicalUrl?.message} />
          </div>
          <Button type="submit">
            <FileCode2 className="h-4 w-4" />
            Generate tags
          </Button>
        </form>
      }
      output={<CodeOutput value={output} placeholder="Meta tags will appear here." />}
    />
  );
}

const ogSchema = z.object({
  title: z.string().trim().min(1, "Title is required."),
  description: z.string().trim().min(1, "Description is required."),
  imageUrl: optionalUrl,
  siteName: z.string().trim().optional(),
  url: optionalUrl,
});

type OgFormValues = z.infer<typeof ogSchema>;

function OpenGraphCard({ values }: { values: OgFormValues }) {
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <div
        className="flex aspect-[1.91/1] items-center justify-center bg-zinc-100 bg-cover bg-center text-sm text-zinc-500"
        style={values.imageUrl ? { backgroundImage: `url("${values.imageUrl}")` } : undefined}
      >
        {!values.imageUrl ? "Preview image" : null}
      </div>
      <div className="space-y-1 p-4">
        <p className="text-xs uppercase tracking-wide text-zinc-400">
          {values.siteName || values.url || "example.com"}
        </p>
        <h3 className="line-clamp-2 text-base font-semibold text-zinc-950">{values.title}</h3>
        <p className="line-clamp-2 text-sm leading-5 text-zinc-600">{values.description}</p>
      </div>
    </div>
  );
}

export function OpenGraphPreviewTool() {
  const [preview, setPreview] = useState<OgFormValues | null>(null);
  const [output, setOutput] = useState("");
  const form = useForm<OgFormValues>({
    resolver: zodResolver(ogSchema),
    defaultValues: {
      title: "",
      description: "",
      imageUrl: "",
      siteName: "",
      url: "",
    },
  });
  const errors = form.formState.errors;

  const submit = (values: OgFormValues) => {
    setPreview(values);
    setOutput(generateMetaTags(values as MetaTagInput));
  };

  return (
    <InputOutputPanel
      inputTitle="OpenGraph fields"
      outputTitle="Preview and tags"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              form.reset({
                title: "LocalTools",
                description: "Privacy-first browser tools for developers and makers.",
                imageUrl: "",
                siteName: "LocalTools",
                  url: "https://local-dev-tools.com",
              })
            }
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              form.reset();
              setPreview(null);
              setOutput("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} label="Copy tags" />}
      input={
        <form className="space-y-4" onSubmit={form.handleSubmit(submit)}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="og-title">Title</Label>
              <Input id="og-title" {...form.register("title")} />
              <ErrorMessage message={errors.title?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="og-site">Site name</Label>
              <Input id="og-site" {...form.register("siteName")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="og-description">Description</Label>
            <Textarea
              id="og-description"
              className="min-h-28 font-sans"
              {...form.register("description")}
            />
            <ErrorMessage message={errors.description?.message} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="og-image">Image URL</Label>
              <Input id="og-image" {...form.register("imageUrl")} />
              <ErrorMessage message={errors.imageUrl?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="og-url">URL</Label>
              <Input id="og-url" {...form.register("url")} />
              <ErrorMessage message={errors.url?.message} />
            </div>
          </div>
          <Button type="submit">
            <Eye className="h-4 w-4" />
            Preview card
          </Button>
        </form>
      }
      output={
        preview ? (
          <div className="space-y-4">
            <OpenGraphCard values={preview} />
            <CodeOutput value={output} className="min-h-0" />
          </div>
        ) : (
          <CodeOutput placeholder="Social preview and OpenGraph tags will appear here." />
        )
      }
    />
  );
}

export function RobotsGeneratorTool() {
  const [preset, setPreset] = useState<RobotsPreset>("allow-all");
  const [paths, setPaths] = useState("");
  const output = useMemo(
    () =>
      generateRobotsTxt(
        preset,
        paths
          .split(/\r?\n/)
          .map((path) => path.trim())
          .filter(Boolean),
      ),
    [paths, preset],
  );

  return (
    <InputOutputPanel
      inputTitle="Rules"
      outputTitle="robots.txt"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              setPreset("custom");
              setPaths("/admin\n/private\n/search");
            }}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setPreset("allow-all");
              setPaths("");
            }}
          />
        </>
      }
      outputActions={<CopyButton value={output} />}
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="robots-preset">Preset</Label>
            <Select
              id="robots-preset"
              value={preset}
              onChange={(event) => setPreset(event.target.value as RobotsPreset)}
            >
              <option value="allow-all">Allow all</option>
              <option value="disallow-all">Disallow all</option>
              <option value="custom">Disallow specific paths</option>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="robots-paths">Paths to disallow</Label>
            <Textarea
              id="robots-paths"
              value={paths}
              onChange={(event) => setPaths(event.target.value)}
              placeholder="/admin&#10;/private"
              disabled={preset !== "custom"}
              className="min-h-40"
            />
            <p className="text-sm text-zinc-500">One path per line. Enabled for the custom preset.</p>
          </div>
        </div>
      }
      output={<CodeOutput value={output} />}
    />
  );
}

export function DownloadLink({ href }: { href: string }) {
  return (
    <a className={buttonVariants({ variant: "secondary", size: "sm" })} href={href} download>
      Download
    </a>
  );
}
